import { confirmProblemReport } from '../../api/generated/problem-reports/problem-reports'
import type { ProblemReportConfirmation } from '../../api/generated/model/problemReportConfirmation'
import {
  reportHostState,
  reportRequestContext,
  settleReportRequest,
  subscribeReportHost,
} from '../../lib/problem-reporting/context'
import { submissionError, type SubmissionError } from './submission'
export function createOwnConfirmation() {
  type State = {
    id: string | null
    explanation: string
    outcome: 'idle' | 'pending' | 'unknown' | 'conflict' | 'saved' | 'rejected'
    error: SubmissionError | null
    acknowledgedRevision: number | null
  }
  let state: State = {
    id: null,
    explanation: '',
    outcome: 'idle',
    error: null,
    acknowledgedRevision: null,
  }
  let frozen: {
    id: string
    body: ProblemReportConfirmation
    context: ReturnType<typeof reportRequestContext>
    uncertain: boolean
  } | null = null
  let generation = reportHostState().generation
  let request: AbortController | null = null
  let disposed = false
  const listeners = new Set<() => void>()
  const publish = (patch: Partial<State>) => {
    if (!disposed) {
      state = { ...state, ...patch }
      listeners.forEach((listener) => listener())
    }
  }
  const stop = subscribeReportHost(() => {
    if (generation !== reportHostState().generation) {
      generation = reportHostState().generation
      request?.abort()
      request = null
      frozen = null
      publish({
        id: null,
        explanation: '',
        outcome: 'idle',
        error: null,
        acknowledgedRevision: null,
      })
    }
  })
  return {
    getState: () => state,
    subscribe: (listener: () => void) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    reconcile(id: string, revision: number) {
      if (
        state.id === id &&
        state.outcome === 'saved' &&
        state.acknowledgedRevision !== null &&
        revision > state.acknowledgedRevision
      ) {
        publish({
          id: null,
          explanation: '',
          outcome: 'idle',
          error: null,
          acknowledgedRevision: null,
        })
      }
    },
    edit(id: string, explanation: string) {
      if (!frozen)
        publish({
          id,
          explanation,
          outcome: 'idle',
          error: null,
          acknowledgedRevision: null,
        })
    },
    async send(
      id: string,
      revision: number,
      result: ProblemReportConfirmation['result'],
    ) {
      if (disposed || request) return
      if (!frozen) {
        const host = reportHostState()
        if (!host.identity || !host.policy?.enabled || !host.policy.can_self)
          return
        const explanation = state.id === id ? state.explanation.trim() : ''
        if (
          result === 'still_broken' &&
          ([...explanation].length < 10 || [...explanation].length > 5000)
        ) {
          publish({ id, error: { code: 'invalid_description' } })
          return
        }
        frozen = {
          id,
          body: {
            command_key: crypto.randomUUID(),
            expected_revision: revision,
            result,
            ...(result === 'still_broken' ? { explanation } : {}),
          },
          context: reportRequestContext(),
          uncertain: false,
        }
      }
      const attempt = frozen
      const controller = new AbortController()
      request = controller
      publish({
        id: attempt.id,
        outcome: 'pending',
        error: null,
        acknowledgedRevision: null,
      })
      try {
        const response = await confirmProblemReport(attempt.id, attempt.body, {
          signal: controller.signal,
          headers: {
            'X-Problem-Report-Context': attempt.context.identity.membershipId,
          },
        })
        await settleReportRequest(attempt.context, controller.signal)
        if (response.status !== 200)
          throw { status: response.status, problem: response.data }
        frozen = null
        publish({
          outcome: 'saved',
          acknowledgedRevision: response.data.applied_revision,
        })
      } catch (error) {
        if (
          disposed ||
          controller.signal.aborted ||
          generation !== attempt.context.generation
        )
          return
        const safe = submissionError(error)
        if (
          attempt.uncertain ||
          !safe.status ||
          safe.status >= 500 ||
          safe.status === 408
        ) {
          attempt.uncertain = true
          publish({ outcome: 'unknown', error: safe })
        } else {
          frozen = null
          publish({
            outcome: safe.status === 409 ? 'conflict' : 'rejected',
            error: safe,
          })
        }
      } finally {
        if (request === controller) request = null
      }
    },
    dispose() {
      disposed = true
      stop()
      request?.abort()
      frozen = null
      listeners.clear()
    },
  }
}
